import { useEffect, useRef, useState, type FormEvent } from 'react';
import { appReleasesApi, type AppRelease } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { adminNavItems } from '@/pages/navItems';
import { useConfirm, useToast } from '@/components/Feedback';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';
import { formatBytes, formatDateTime } from '@/lib/format';

const EMPTY = { versionCode: '', versionName: '', notes: '', mandatory: false, minSupportedVersionCode: '' };

const STATUS_LABELS: Record<AppRelease['status'], string> = {
  draft: 'Not published',
  published: 'Published',
  withdrawn: 'Withdrawn',
};

/** QR code for a URL, drawn as SVG in the browser (no external service). */
function QrCode({ value, size = 168 }: { value: string; size?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    import('@zxing/browser')
      .then(({ BrowserQRCodeSvgWriter }) => {
        if (cancelled || !ref.current) return;
        const svg = new BrowserQRCodeSvgWriter().write(value, size, size);
        svg.setAttribute('role', 'img');
        svg.setAttribute('aria-label', `QR code for ${value}`);
        ref.current.replaceChildren(svg);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [value, size]);
  if (failed) return <p className="text-muted">QR code unavailable — share the link instead.</p>;
  return <div ref={ref} className="qr-box" style={{ width: size, height: size }} />;
}

export function AdminCrewApp() {
  const { data: releases, loading, error, reload } = useAsync(() => appReleasesApi.list(), []);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { flash, ok: showOk, error: showError, clear: clearFlash } = useFlash();
  const [actionError, setActionError] = useState('');
  const confirm = useConfirm();
  const toast = useToast();

  const installUrl = `${window.location.origin}/crew-app`;
  const live = (releases ?? []).find((r) => r.status === 'published');
  const highest = (releases ?? []).reduce((max, r) => Math.max(max, r.versionCode), 0);

  const upload = async (e: FormEvent) => {
    e.preventDefault();
    clearFlash();
    if (!file) {
      showError('Choose the APK file to upload.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.apk')) {
      showError('That file is not an APK. Choose the app build ending in .apk.');
      return;
    }
    if (file.size > 150 * 1024 * 1024) {
      showError('The APK is larger than 150 MB.');
      return;
    }
    const versionCode = Number(form.versionCode);
    if (!Number.isInteger(versionCode) || versionCode < 1) {
      showError('Enter the version code (a whole number from the app build).');
      return;
    }
    setProgress(0);
    try {
      const created = await appReleasesApi.upload(
        {
          file,
          versionCode,
          versionName: form.versionName.trim(),
          notes: form.notes.trim(),
          mandatory: form.mandatory,
          minSupportedVersionCode: form.minSupportedVersionCode ? Number(form.minSupportedVersionCode) : null,
        },
        setProgress,
      );
      showOk(`Version ${created.versionName} uploaded. Publish it below when you are ready for crew to install it.`);
      toast(`Version ${created.versionName} uploaded`);
      setForm(EMPTY);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      reload();
    } catch (err) {
      showError(errorText(err, 'The upload did not finish.'));
    } finally {
      setProgress(null);
    }
  };

  const publish = async (r: AppRelease) => {
    const ok = await confirm({
      title: `Publish version ${r.versionName}?`,
      message: r.mandatory
        ? `Crew will be told to update now — this version is marked as a required update. The install page will offer ${r.versionName}.`
        : `Crew will be offered this update the next time they open the app, and the install page will offer ${r.versionName}.`,
      confirmLabel: 'Publish',
    });
    if (!ok) return;
    setActionError('');
    try {
      await appReleasesApi.publish(r.id);
      toast(`Version ${r.versionName} published`);
      reload();
    } catch (err) {
      setActionError(errorText(err, 'Could not publish this version.'));
    }
  };

  const withdraw = async (r: AppRelease) => {
    const ok = await confirm({
      title: `Withdraw version ${r.versionName}?`,
      message:
        'It will no longer be offered for download or as an update. Phones that already installed it keep it. The newest remaining published version is offered instead.',
      confirmLabel: 'Withdraw',
      danger: true,
    });
    if (!ok) return;
    setActionError('');
    try {
      await appReleasesApi.withdraw(r.id);
      toast(`Version ${r.versionName} withdrawn`);
      reload();
    } catch (err) {
      setActionError(errorText(err, 'Could not withdraw this version.'));
    }
  };

  const remove = async (r: AppRelease) => {
    const ok = await confirm({
      title: `Delete upload ${r.versionName}?`,
      message: 'This upload was never published. Deleting it removes the file for good.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setActionError('');
    try {
      await appReleasesApi.remove(r.id);
      toast(`Upload ${r.versionName} deleted`);
      reload();
    } catch (err) {
      setActionError(errorText(err, 'Could not delete this upload.'));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(installUrl);
      toast('Install link copied');
    } catch {
      toast('Could not copy — select the link and copy it instead', 'error');
    }
  };

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header">
          <h1>Crew app</h1>
          <p className="text-muted">
            Upload new versions of the DispatchDash crew app for Android and share the install page with crew.
          </p>
        </div>

        <div className="crewapp-grid">
          <section className="crewapp-card">
            <h2>Share with crew</h2>
            <p className="text-muted">
              Crew open this page on their Android phone, download the app and sign in with their crew email.
            </p>
            <div className="share-row">
              <QrCode value={installUrl} />
              <div className="share-text">
                <a href={installUrl} target="_blank" rel="noreferrer" className="share-link">{installUrl}</a>
                <Button type="button" size="sm" variant="secondary" onClick={() => void copyLink()}>Copy link</Button>
                <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                  {live
                    ? `Currently offering version ${live.versionName} (${formatBytes(live.sizeBytes)}).`
                    : 'No version is published yet, so the install page has nothing to download.'}
                </p>
              </div>
            </div>
          </section>

          <section className="crewapp-card">
            <h2>Upload a new version</h2>
            <form onSubmit={upload} className="upload-form">
              <label className="file-field">
                <span>App file (.apk)</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".apk,application/vnd.android.package-archive"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {file && <span className="text-muted">{file.name} · {formatBytes(file.size)}</span>}
              </label>
              <div className="grid-2">
                <Input
                  label="Version code"
                  type="number"
                  min={1}
                  value={form.versionCode}
                  onChange={(e) => setForm({ ...form, versionCode: e.target.value })}
                  hint={highest ? `Must be higher than ${highest}` : 'versionCode from the app build'}
                  required
                />
                <Input
                  label="Version name"
                  value={form.versionName}
                  onChange={(e) => setForm({ ...form, versionName: e.target.value })}
                  placeholder="1.0.0"
                  required
                />
              </div>
              <label className="notes-field">
                <span>What's new (shown to crew)</span>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Faster photo uploads. Fixed sign-in on older phones."
                />
              </label>
              <div className="grid-2">
                <label className="check-field">
                  <input
                    type="checkbox"
                    checked={form.mandatory}
                    onChange={(e) => setForm({ ...form, mandatory: e.target.checked })}
                  />
                  <span>Required update — crew must install it to keep using the app</span>
                </label>
                <Input
                  label="Oldest version still allowed (optional)"
                  type="number"
                  min={0}
                  value={form.minSupportedVersionCode}
                  onChange={(e) => setForm({ ...form, minSupportedVersionCode: e.target.value })}
                  hint="Phones on an older version code must update"
                />
              </div>
              {progress != null && (
                <div className="upload-progress" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                  <div className="upload-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
                  <span>{progress < 1 ? `Uploading… ${Math.round(progress * 100)}%` : 'Checking the file…'}</span>
                </div>
              )}
              <FlashMessage flash={flash} />
              <Button type="submit" loading={progress != null}>Upload</Button>
            </form>
          </section>
        </div>

        <h2 className="releases-heading">Versions</h2>
        {actionError && <p className="text-urgent">{actionError}</p>}
        <DataTable
          data={releases ?? []}
          keyField="id"
          loading={loading}
          loadError={error}
          onRetry={reload}
          dense
          emptyMessage="No versions uploaded yet."
          columns={[
            {
              key: 'versionName',
              header: 'Version',
              render: (r: AppRelease) => (
                <span>
                  <strong>{r.versionName}</strong> <span className="text-muted">(code {r.versionCode})</span>
                </span>
              ),
            },
            { key: 'sizeBytes', header: 'Size', align: 'right', render: (r: AppRelease) => formatBytes(r.sizeBytes) },
            {
              key: 'status',
              header: 'Status',
              render: (r: AppRelease) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} size="sm" />,
            },
            {
              key: 'publishedAt',
              header: 'Published',
              render: (r: AppRelease) => (r.publishedAt ? formatDateTime(r.publishedAt) : '—'),
            },
            { key: 'mandatory', header: 'Required', render: (r: AppRelease) => (r.mandatory ? 'Yes' : 'No') },
            {
              key: 'download',
              header: 'Download',
              render: (r: AppRelease) =>
                r.downloadPath ? <a href={r.downloadPath}>Download</a> : <span className="text-muted">—</span>,
            },
            {
              key: 'actions',
              header: '',
              sortable: false,
              align: 'right',
              render: (r: AppRelease) => (
                <div className="row-actions">
                  {r.status !== 'published' && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => void publish(r)}>
                      {r.status === 'withdrawn' ? 'Publish again' : 'Publish'}
                    </Button>
                  )}
                  {r.status === 'published' && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => void withdraw(r)}>Withdraw</Button>
                  )}
                  {r.status === 'draft' && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => void remove(r)}>Delete</Button>
                  )}
                </div>
              ),
            },
          ]}
        />

        <style>{`
          .crewapp-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-lg);
            align-items: start;
          }
          @media (max-width: 900px) { .crewapp-grid { grid-template-columns: 1fr; } }
          .crewapp-card {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-lg);
            display: flex;
            flex-direction: column;
            gap: var(--space-sm);
          }
          .crewapp-card h2 { font-size: 1.125rem; color: var(--color-forest); margin: 0; }
          .share-row { display: flex; gap: var(--space-lg); align-items: flex-start; flex-wrap: wrap; }
          .share-text { display: flex; flex-direction: column; gap: var(--space-sm); align-items: flex-start; min-width: 0; flex: 1; }
          .share-link { word-break: break-all; font-weight: 600; }
          .qr-box { background: #fff; border: 1px solid var(--color-border); border-radius: var(--radius-sm); flex-shrink: 0; }
          .qr-box svg { display: block; }
          .upload-form { display: flex; flex-direction: column; gap: var(--space-md); }
          .file-field, .notes-field { display: flex; flex-direction: column; gap: 4px; font-size: 0.875rem; font-weight: 500; }
          .notes-field textarea {
            font: inherit;
            font-weight: 400;
            padding: var(--space-sm);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-sm);
            resize: vertical;
          }
          .check-field { display: flex; gap: var(--space-sm); align-items: flex-start; font-size: 0.875rem; padding-top: 1.5rem; }
          .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-md); }
          @media (max-width: 640px) { .grid-2 { grid-template-columns: 1fr; } .check-field { padding-top: 0; } }
          .upload-progress {
            position: relative;
            height: 28px;
            background: var(--color-sand);
            border-radius: var(--radius-sm);
            overflow: hidden;
            font-size: 0.8125rem;
          }
          .upload-bar { position: absolute; inset: 0 auto 0 0; background: var(--color-signal); transition: width 0.2s; }
          .upload-progress span { position: relative; line-height: 28px; padding-left: var(--space-sm); }
          .releases-heading { font-size: 1.125rem; margin: var(--space-xl) 0 var(--space-sm); }
          .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; flex-wrap: wrap; }
        `}</style>
      </div>
    </Layout>
  );
}
